import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import ExpandableSection from "@/components/job/ExpandableSection";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep } from "@/constants/inputs";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

const toSafeString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
};

export function MinimumClearance() {
  const { sections, setChecks } = useCUMechanicalJobStore(
    useShallow((state) => ({
      sections: state.sections,
      setChecks: state.setChecks,
    }))
  );

  const checks = sections.checks;
  const repairType = sections.repairType.data.repairType;
  const isTee = repairType === "tee";
  const [isExpanded, setIsExpanded] = useState(true);
  const [minIdClearance, setMinIdClearance] = useState("");
  const [minIdClearance2, setMinIdClearance2] = useState("");

  const needsMinimumClearance = checks.data.do_you_need_minimum_clearance === true;
  const shouldShow = checks.isDataFilled && needsMinimumClearance;

  useEffect(() => {
    if (!shouldShow) {
      setMinIdClearance("");
      setMinIdClearance2("");
      return;
    }
    setMinIdClearance(toSafeString(checks.data.min_id_clearance));
    setMinIdClearance2(toSafeString((checks.data as { min_id_clearance_2?: number | null }).min_id_clearance_2));
  }, [checks.data, shouldShow]);

  const minIdClearanceValid = !needsMinimumClearance || minIdClearance.trim().length > 0;
  const minIdClearance2Valid = !isTee || !needsMinimumClearance || minIdClearance2.trim().length > 0;
  const canSave = minIdClearanceValid && minIdClearance2Valid;

  const isComplete =
    !needsMinimumClearance ||
    (checks.data.min_id_clearance !== null &&
      checks.data.min_id_clearance !== undefined &&
      (!isTee ||
        ((checks.data as { min_id_clearance_2?: number | null }).min_id_clearance_2 !== null &&
          (checks.data as { min_id_clearance_2?: number | null }).min_id_clearance_2 !== undefined)));

  const status = !shouldShow ? "hidden" : isComplete ? "done" : "open";

  const handleSave = () => {
    setChecks({
      data: {
        ...checks.data,
        min_id_clearance:
          needsMinimumClearance && minIdClearance.trim().length > 0 ? Number.parseFloat(minIdClearance) : null,
        ...(isTee && {
          min_id_clearance_2:
            needsMinimumClearance && minIdClearance2.trim().length > 0 ? Number.parseFloat(minIdClearance2) : null,
        }),
      },
      status: "done",
    });
    setIsExpanded(false);
  };

  return (
    <ExpandableSection
      isExpanded={isExpanded}
      setIsExpanded={() => setIsExpanded((prev) => !prev)}
      status={status}
      title="Minimum Clearance"
    >
      <div className="flex flex-col gap-3">
        {needsMinimumClearance && (
          <TextInput
            label="Minimum ID Clearance"
            type="number"
            step={inputStep}
            placeholder="Enter minimum ID clearance"
            value={minIdClearance}
            endAdornment={
              <Typography variant="text-small" weight="regular" className="text-gray-400">
                in
              </Typography>
            }
            onChange={(e) => {
              setMinIdClearance(toSafeString(e.currentTarget.value));
            }}
          />
        )}

        {isTee && needsMinimumClearance && (
          <TextInput
            label="Minimum ID Clearance 2"
            type="number"
            step={inputStep}
            placeholder="Enter second minimum ID clearance"
            value={minIdClearance2}
            endAdornment={
              <Typography variant="text-small" weight="regular" className="text-gray-400">
                in
              </Typography>
            }
            onChange={(e) => {
              setMinIdClearance2(toSafeString(e.currentTarget.value));
            }}
          />
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={handleSave} disabled={!canSave}>
          Save
        </Button>
      </div>
    </ExpandableSection>
  );
}
